<script lang="ts" setup>
import { ref, watch, onMounted, computed, nextTick } from 'vue'
import RoomieInput, { STATUS as INPUT_STATUS } from '@lahaus-roomie/roomie/src/components/RoomieInput/index.vue'
import RoomieLoader from '@lahaus-roomie/roomie/src/components/RoomieLoader/index.vue'
import RoomieChipSelector from '@lahaus-roomie/roomie/src/components/RoomieChipSelector/index.vue'
import IconSend from '@lahaus-roomie/roomie/src/assets/icons/v2/next.svg'
import { useI18n } from 'vue-i18n'
import type { Props } from './types.ts'
import './_index.scss'
import AppBubbleAuthor from '@/components/App/ChatV2/BubbleAuthor/index.vue'
import AppChatMigratedAttachments from '@/components/App/ChatV2/Migrated/Attachments/index.vue'
import AppChatRecommendations from '@/components/App/ChatV2/Recommendations/index.vue'
import AppChatScheduleTurnSelection from '@/components/App/ChatV2/ScheduleTurnSelection/index.vue'
import AppChatContactMethodSelection from '@/components/App/ChatV2/ContactMethodSelection/index.vue'
import AppChatReferralSection from '@/components/App/ChatV2/ReferralSection/index.vue'
import type { AppMessage, UserMessage } from '@/composables/useSamGptCompletions/types'
import { useAppChatStore } from '@/stores/appChatStore'
import { delay } from '@/utils/delay'
import { calculateEnterpriseId } from '@/components/App/ChatV2/utils'

const props = defineProps<Props>()
const { t } = useI18n()
const emit = defineEmits(['error'])

const currentMessage = ref('')
const chatInput = ref(null)
const autoScrollEnabled = ref(true)
const highlightedMessageId = ref<string | null>(null)
const appChatStore = useAppChatStore()
const HIGHLIGHT_START_DELAY_MS = 700
const HIGHLIGHT_DURATION_MS = 4000

const { init, messages, showWritingFeedback, isLoading, handleSendMessage, error, addMessage, canSendMessage } = useSamGptCompletions({
  listingIds: [props.listing.code],
  listingName: props.listing.name,
  enterpriseId: calculateEnterpriseId(props.listing)
})

const hasAttachments = (message: AppMessage) => message.attachments?.length

const hasImages = (message: AppMessage) => message.attachments?.some(attachment => attachment.media_type === 'image')
const hasDocuments = (message: AppMessage) => message.attachments?.some(attachment => attachment.media_type === 'document')

const hasRecommendations = (message: AppMessage) => message.recommendations?.length

const resolveBubblesStyles = (author: string) => author === 'user' ? 'app-chat__author-user' : 'app-chat__author-bot'

const sendMessage = () => {
  autoScrollEnabled.value = true
  handleSendMessage({ text: currentMessage.value })
  currentMessage.value = ''
}

const vNoAutoComplete = {
  mounted: (el: HTMLElement) => {
    const inputEl = el.getElementsByClassName('roomie-input')[0]
    inputEl.setAttribute('autocomplete', 'off')
  }
}

const showButtonsOfMessage = (message: AppMessage) => {
  const lastMessageOfChat = messages.value[messages.value.length - 1]
  return lastMessageOfChat?.buttons?.length && lastMessageOfChat === message
}

const handleSelectedButton = (buttonOfMessage: string, messageOfChat: AppMessage) => {
  if (buttonOfMessage.toLowerCase().includes('contactar')) {
    addMessage('user', { text: buttonOfMessage })
    showWritingFeedback.value = true
    setTimeout(() => {
      showWritingFeedback.value = false
      addMessage('bot', {
        text: '',
        additionalComponents: [
          {
            name: 'AppChatContactMethodSelection',
            props: {
              helperText: t('contactMethodSelectionHelperText'),
              is_in_person_visit_active: messageOfChat.metadata?.is_in_person_visit_active
            }
          }
        ]
      })
    }, 1000)
  } else {
    handleSendMessage({ text: buttonOfMessage })
  }
}

const shouldShowAdditionalComponent = (message: AppMessage) => message?.additionalComponents?.length

const disableScheduleCta = computed(() => {
  const lastMessage = messages.value[messages.value.length - 1]
  return lastMessage?.intent === 'SCHEDULE_CREATED' || lastMessage?.intent === 'VISIT' || lastMessage?.intent === 'AFTER_SCHEDULE'
})

const canDisplayMapLocation = (message: AppMessage): boolean => {
  return !!(message.metadata?.location?.latitude && message.metadata?.location?.longitude)
}

const generateStaticMapUrl = (message: AppMessage): string => {
  if (!canDisplayMapLocation(message)) return ''

  const { latitude, longitude } = message.metadata!.location
  return `https://maps.googleapis.com/maps/api/staticmap?center=${latitude},${longitude}&size=400x300&key=${props.googleStaticMapApiKey}&zoom=15&markers=${latitude},${longitude}`
}

const goToMessage = async (messageId: string): Promise<void> => {
  const htmlElement: HTMLElement | null = document.getElementById(`chat-message-user-${messageId}`)
  if (htmlElement) {
    autoScrollEnabled.value = false
    htmlElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
    await nextTick()

    await delay(HIGHLIGHT_START_DELAY_MS)
    highlightedMessageId.value = messageId

    await delay(HIGHLIGHT_DURATION_MS)
    highlightedMessageId.value = null
  }
}

watch(error, () => {
  if (error.value) emit('error')
})

onMounted(async () => {
  await init()

  chatInput.value?.focusInput?.()
})
</script>

<script lang="ts">
export default {
  name: 'AppChatMigratedBubbles',
  components: {
    AppChatContactMethodSelection,
    AppChatScheduleTurnSelection,
    AppChatReferralSection
  }
}
</script>

<template>
  <div class="overflow-hidden h-full relative">
    <div
      v-scroll-bottom="autoScrollEnabled"
      :class="['app-chat__messages-container p-16 flex flex-col overflow-y-auto overflow-x-hidden', {'h-[calc(100%-116px)]': !canSendMessage, 'h-[calc(100%-80px)]': canSendMessage}]">
      <RoomieLoader
        :loading="isLoading"
        size="md"
        class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full" />

      <template
        v-for="message, index in messages"
        :key="index">
        <template v-if="hasAttachments(message) || hasRecommendations(message) || shouldShowAdditionalComponent(message) || message.text">
          <div
            class="mt-24 flex items-end gap-8"
            :class="{ 'flex-row': message.author === 'bot', 'flex-col-reverse': message.author === 'user' }">
            <AppBubbleAuthor
              v-if="!hasRecommendations(message)"
              class="min-w-[32px]"
              :author="message.author"
              :bot-name="botAgentName" />

            <div
              class="flex flex-col gap-16 w-full">
              <div
                v-if="hasImages(message)"
                class="app-chat__bubbles text-14 w-fit shadow-[0_0_1px_#0000002f]"
                :class="resolveBubblesStyles(message.author)">
                <AppChatMigratedAttachments
                  :listing-name="listing.name"
                  :attachments="message.attachments!.filter(attachment => attachment.media_type === 'image')"
                  :screen />
              </div>

              <div
                v-if="hasDocuments(message)"
                class="app-chat__bubbles text-14 w-fit shadow-[0_0_1px_#0000002f]"
                :class="resolveBubblesStyles(message.author)">
                <AppChatMigratedAttachments
                  :listing-name="listing.name"
                  :attachments="message.attachments!.filter(attachment => attachment.media_type === 'document')"
                  :screen />
              </div>

              <div v-if="hasRecommendations(message)">
                <AppChatRecommendations
                  class="mt-24"
                  :listings="message.recommendations!"
                  :screen />
              </div>

              <div
                v-if="canDisplayMapLocation(message)"
                class="flex justify-center app-chat__bubbles app-chat__author-bot">
                <a
                  class="w-full"
                  :href="`https://www.google.com/maps?q=${message.metadata!.location.latitude},${message.metadata!.location.longitude}`"
                  target="_blank"
                  :data-lh-id="`${screen}-app-chat-static-map-${message.metadata!.location.latitude}-${message.metadata!.location.longitude}`"
                  rel="noopener noreferrer nofollow">
                  <img
                    class="w-full object-cover mb-lh-8 pointer-events-none"
                    :src="generateStaticMapUrl(message)"
                    :alt="`Ubicación del proyecto: ${message.metadata?.location.address}`">

                  <p class="cta-14 text-pine-600">{{ t('view_location') }}</p>
                </a>
              </div>

              <div
                v-if="message.text"
                :id="`chat-message-${message.author}-${message.id}`"
                class="app-chat__bubbles scroll-mt-[5rem] text-14 w-fit shadow-[0_0_1px_#0000002f]"
                :class="[resolveBubblesStyles(message.author), {'app-chat__highlighted-message': !!(highlightedMessageId === message.id && message.author === 'user')}]">
                <template v-if="message.question && message.hasReply">
                  <div
                    v-lh-id="{P: screen, Z: 'ventana del chat de sam', A: 'ir al mensaje', C: message.id, I: index }"
                    class="border-l-4 pl-lh-4 mb-lh-8"
                    role="button"
                    tabindex="0"
                    @keydown="goToMessage(message.id!)"
                    @click="goToMessage(message.id!)">
                    <div class="text-sm text-gray-600 font-semibold">
                      {{ t('you') }}
                    </div>

                    <p
                      v-sanitize="message.question"
                      class="body-2-m text-pine-600 truncate" />
                  </div>
                </template>

                <p
                  v-sanitize="message.text"
                  class="body-2-m"
                  :class="{ 'text-pine-600': message.author === 'bot', 'text-white': message.author === 'user' }" />
              </div>

              <template v-if="shouldShowAdditionalComponent(message)">
                <template
                  v-for="(comp, i) in message.additionalComponents"
                  :key="`additional-component-${comp.name}-${i}`">
                  <div
                    class="app-chat__bubbles text-14 w-fit shadow-[0_0_1px_#0000002f] !max-w-[calc(100%-36px)]"
                    :class="resolveBubblesStyles(message.author)">
                    <component
                      :is="comp.name"
                      v-bind="comp.props"
                      :bot-name="botAgentName"
                      :is-schedule-cta-disabled="disableScheduleCta"
                      @contact-method-selected="(message: UserMessage) => handleSendMessage(message)"
                      @full-turn-selected="(message: UserMessage) => handleSendMessage(message)" />
                  </div>
                </template>
              </template>
            </div>
          </div>
        </template>

        <div
          v-if="showButtonsOfMessage(message)"
          class="app-chat__chips-container">
          <RoomieChipSelector
            v-for="(buttonOfMessage) in message.buttons"
            :id="`app-chat-message-button-${buttonOfMessage.toLowerCase().replace(' ', '-')}`"
            :key="`app-chat-message-button-${buttonOfMessage}`"
            :data-lh-id="`${screen}-app-chat-message-button`"
            :is-selected="false"
            @click="handleSelectedButton(buttonOfMessage, message)">
            <template #content>
              <p class="text-14 pointer-events-none">
                {{ buttonOfMessage }}
              </p>
            </template>
          </RoomieChipSelector>
        </div>
      </template>

      <div
        v-if="showWritingFeedback"
        class="text-14 text-primary-600 font-medium">
        <span class="mr-2">
          {{ t('writing') }}
        </span>

        <span
          v-for="index in 3"
          :key="index"
          class="chat-dot mt-24 mr-2 inline-block w-[3px] h-[3px] rounded-full bg-primary-600" />
      </div>
    </div>

    <form
      v-if="!isLoading && !appChatStore.hideChatInputField"
      class="app-chat__send-container w-full p-16 flex items-center relative"
      @submit.prevent="sendMessage">
      <RoomieInput
        id="app-chat-input-send-message"
        ref="chatInput"
        v-model="currentMessage"
        v-no-auto-complete
        data-lh-id="app-chat-input-send-message"
        :is-internal-validate="false"
        :status="!canSendMessage ? INPUT_STATUS.error : INPUT_STATUS.default"
        :is-disabled="!canSendMessage"
        :helper-text="canSendMessage ? '' : t('messageLimitReached')"
        :value="currentMessage" />

      <button
        id="app-chat-send-button"
        data-lh-id="app-chat-send-button"
        :aria-label="t('sendMessageCta')"
        type="submit"
        class="absolute top-1/2 -translate-y-1/2 bg-pine-600 min-w-32 h-32 max-h-32 rounded-full">
        <div class="flex items-center justify-center">
          <IconSend
            class="fill-spring-600 pointer-events-none origin-center -rotate-90"
            :class="{ 'fill-spring-600': currentMessage.length }" />
        </div>
      </button>
    </form>
  </div>
</template>

<i18n src="./i18n.json" lang="json" />
